import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, Wallet, ArrowUpRight, ArrowDownRight, PieChartIcon } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';

interface CryptoPortfolioOverviewProps {
  aggregatedHoldings: AggregatedCryptoHolding[];
  prices: CryptoPrice[];
  pricesLoading?: boolean;
  baseCurrency: string;
}

export const CryptoPortfolioOverview: React.FC<CryptoPortfolioOverviewProps> = ({
  aggregatedHoldings,
  prices,
  pricesLoading = false,
  baseCurrency,
}) => {
  // Use aggregated amounts × price for total value (Requirement 9.5)
  const totalValue = aggregatedHoldings.reduce((acc, agg) => {
    const price = prices.find(p => p.symbol === agg.symbol)?.price || 0;
    return acc + agg.totalAmount * price;
  }, 0);

  const totalCostBasis = aggregatedHoldings.reduce((acc, agg) => acc + agg.totalCostBasis, 0);
  const totalProfitLoss = totalValue - totalCostBasis;
  const profitLossPercentage = totalCostBasis > 0 ? (totalProfitLoss / totalCostBasis) * 100 : 0;
  const isProfit = totalProfitLoss >= 0;

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: baseCurrency }).format(value);

  const topPerformer = prices.length > 0
    ? [...prices].sort((a, b) => b.change_24h - a.change_24h)[0]
    : null;

  const allocationData = aggregatedHoldings
    .map(agg => {
      const price = prices.find(p => p.symbol === agg.symbol)?.price || 0;
      return {
        name: agg.symbol,
        value: agg.totalAmount * price,
      };
    })
    .filter(d => d.value > 0)
    .sort((a, b) => b.value - a.value);

  const COLORS = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
    '#82ca9d',
    '#8884d8'
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Portfolio Value</CardTitle>
          <Wallet className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {pricesLoading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
          )}
          <p className="text-xs text-muted-foreground mt-1">
            Across {aggregatedHoldings.length} asset{aggregatedHoldings.length !== 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Profit/Loss</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {pricesLoading ? (
            <>
              <Skeleton className="h-8 w-28" />
              <Skeleton className="h-4 w-16 mt-1" />
            </>
          ) : (
            <>
              <div className={`text-2xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                {isProfit ? '+' : ''}{formatCurrency(totalProfitLoss)}
              </div>
              <div className={`flex items-center text-xs ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                {isProfit ? <ArrowUpRight className="mr-1 h-3 w-3" /> : <ArrowDownRight className="mr-1 h-3 w-3" />}
                {profitLossPercentage.toFixed(2)}%
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Top Performer</CardTitle>
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          {pricesLoading ? (
            <>
              <Skeleton className="h-8 w-16" />
              <Skeleton className="h-4 w-24 mt-1" />
            </>
          ) : topPerformer ? (
            <>
              <div className="text-2xl font-bold">{topPerformer.symbol}</div>
              <p className={`text-xs ${topPerformer.change_24h >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {topPerformer.change_24h >= 0 ? '+' : ''}{topPerformer.change_24h.toFixed(2)}% (24h)
              </p>
            </>
          ) : (
            <div className="text-2xl font-bold">N/A</div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Portfolio Allocation Chart */}
      {allocationData.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Portfolio Allocation</CardTitle>
            <PieChartIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={allocationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {allocationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Legend verticalAlign="bottom" height={36} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
